import React, { useState } from 'react';
import { MapContainer, TileLayer, Polyline, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './MapWorkspace.css';

// Fix Leaflet default icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
    iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
    iconUrl: require('leaflet/dist/images/marker-icon.png'),
    shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to handle map interactions via hooks
const MapController = ({ items, center, zoom, routeCoordinates, tripId }) => {
    const map = useMap();
    const lastTripId = React.useRef(null);

    // Auto-fit bounds or cinematic zoom logic
    React.useEffect(() => {
        if (!routeCoordinates || routeCoordinates.length === 0) return;

        // If it's a new trip generation (different tripId than last time)
        if (tripId && tripId !== lastTripId.current) {
            lastTripId.current = tripId;
            
            const startCoord = routeCoordinates[0];
            
            // Step 1: Briefly fit bounds to show the overview
            const bounds = L.latLngBounds(routeCoordinates);
            map.fitBounds(bounds, { padding: [50, 50], animate: false });

            // Step 2: Cinematic zoom into the starting location
            // Small delay to ensure fitBounds completed and user sees the context
            setTimeout(() => {
                map.flyTo(startCoord, 14, {
                    duration: 2.5,
                    easeLinearity: 0.25
                });
            }, 500);
        } else if (!tripId) {
            // Standard fitting if no trip ID (e.g., static view update)
            const bounds = L.latLngBounds(routeCoordinates);
            map.fitBounds(bounds, { padding: [50, 50] });
        }
    }, [routeCoordinates, map, tripId]);

    return null;
};

const MapWorkspace = ({ tripPlan }) => {
    const defaultCenter = [39.8283, -98.5795]; // Center of US
    const defaultZoom = 4;
    
    // Layer visibility toggles
    const [showWeather, setShowWeather] = useState(true);
    const [showTraffic, setShowTraffic] = useState(true);
    const [showFuelStations, setShowFuelStations] = useState(true);
    const [showRestAreas, setShowRestAreas] = useState(false);
    const [showRouteAlerts, setShowRouteAlerts] = useState(true);
    const [showFuelError, setShowFuelError] = useState(true);

    // Reset alert visibility when trip changes
    React.useEffect(() => {
        if (tripPlan?.id) {
            setShowRouteAlerts(true);
            setShowFuelError(true);
        }
    }, [tripPlan?.id]);

    const routeCoordinates = tripPlan?.route_geojson?.geometry?.coordinates?.map(
        coord => [coord[1], coord[0]]
    ) || [];

    const stops = tripPlan?.stops || [];
    const weatherData = tripPlan?.weather_data || {};
    const trafficData = tripPlan?.traffic_data || {};
    const fuelStations = tripPlan?.fuel_stations || [];
    const restAreas = tripPlan?.rest_areas || [];
    const fuelStationError = trafficData?.fuel_station_error;

    // Custom marker icons
    const createIcon = (color) => L.divIcon({
        className: 'custom-marker',
        html: `<div style="background: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
        iconSize: [16, 16],
        iconAnchor: [8, 8]
    });

    // We need a wrapper to access useMap for the controls
    const WrappedMapControls = () => {
        const map = useMap();
        
        const handleZoomIn = (e) => {
            e.stopPropagation();
            map.zoomIn();
        };

        const handleZoomOut = (e) => {
            e.stopPropagation();
            map.zoomOut();
        };

        const handleLocate = (e) => {
            e.stopPropagation();
            map.locate({ setView: true, maxZoom: 12 });
            
            // Add handler for location found
            map.once('locationfound', (e) => {
                L.marker(e.latlng, {
                    icon: L.divIcon({
                        className: 'current-loc-marker',
                        html: '<div style="background: #2563EB; width: 16px; height: 16px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 0 2px #2563EB;"></div>',
                        iconSize: [20, 20],
                        iconAnchor: [10, 10]
                    })
                }).addTo(map).bindPopup("You are here").openPopup();
            });
        };

        return (
            <div className="leaflet-bottom leaflet-right" style={{ pointerEvents: 'none', marginBottom: '80px', marginRight: '10px' }}>
                <div style={{ pointerEvents: 'auto' }}>
                    <div className="map-controls-embedded">
                        <button className="map-btn" onClick={handleZoomIn}><span className="material-symbols-outlined">add</span></button>
                        <button className="map-btn" onClick={handleZoomOut}><span className="material-symbols-outlined">remove</span></button>
                        <button className="map-btn" style={{ marginTop: '12px' }} onClick={handleLocate}><span className="material-symbols-outlined">my_location</span></button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="map-workspace-v2">
            <MapContainer
                center={routeCoordinates.length > 0 ? routeCoordinates[0] : defaultCenter}
                zoom={routeCoordinates.length > 0 ? 6 : defaultZoom}
                className="map-container"
                zoomControl={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />

                <MapController routeCoordinates={routeCoordinates} tripId={tripPlan?.id} />
                <WrappedMapControls />

                {/* Route Line */}
                {routeCoordinates.length > 0 && (
                    <Polyline
                        positions={routeCoordinates}
                        pathOptions={{ 
                            color: '#2D8A56', 
                            weight: 4,
                            opacity: 0.8
                        }}
                    />
                )}

                {/* Stop Markers */}
                {stops.map((stop, idx) => {
                    const color = stop.stop_type === 'FUEL' ? '#F59E0B' : 
                                  stop.stop_type === 'REST_30_MIN' ? '#3B82F6' : 
                                  stop.stop_type === 'SLEEPER_BERTH' ? '#8B5CF6' : 
                                  stop.stop_type === 'START' ? '#10B981' :
                                  stop.stop_type === 'PICKUP' ? '#10B981' :
                                  stop.stop_type === 'DROPOFF' ? '#EF4444' : '#2D8A56';
                    
                    const formatTime = (isoString) => {
                        return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    };

                    const getLabel = (type) => {
                        return type.replace(/_/g, ' ');
                    };

                    // Use POI name if available from OSM
                    const stopName = stop.name || getLabel(stop.stop_type);
                    const isOsmData = stop.source === 'osm';

                    return (
                        <Marker 
                            key={idx} 
                            position={[stop.location_geo?.lat || 0, stop.location_geo?.lon || 0]}
                            icon={createIcon(color)}
                        >
                            <Popup className="stop-popup">
                                <div style={{ padding: '4px' }}>
                                    <strong style={{ fontSize: '1.1em', color: '#1E293B', textTransform: 'uppercase' }}>
                                        {stopName}
                                    </strong>
                                    {stop.brand && (
                                        <div style={{ fontSize: '0.85em', color: '#64748B', marginTop: '2px' }}>
                                            {stop.brand}
                                        </div>
                                    )}
                                    {isOsmData && (
                                        <div style={{ fontSize: '0.7em', color: '#10B981', marginTop: '4px' }}>
                                            ✓ Real location from OpenStreetMap
                                        </div>
                                    )}
                                    <div style={{ marginTop: '8px', color: '#64748B', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>schedule</span>
                                            <span>Est. Arrival: <strong>{formatTime(stop.timestamp)}</strong></span>
                                        </div>
                                        {stop.duration_minutes > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>timer</span>
                                                <span>Duration: <strong>{stop.duration_minutes} mins</strong></span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Popup>
                        </Marker>
                    );
                })}

                {/* Weather Markers */}
                {showWeather && weatherData?.points?.map((point, idx) => (
                    <Marker 
                        key={`weather-${idx}`} 
                        position={[point.lat, point.lon]}
                        icon={L.divIcon({
                            className: 'weather-marker',
                            html: `<div style="background: rgba(59, 130, 246, 0.9); color: white; padding: 4px 8px; border-radius: 12px; font-size: 11px; font-weight: bold; white-space: nowrap; box-shadow: 0 2px 8px rgba(0,0,0,0.2);">
                                ${Math.round(point.temperature || 0)}°F
                            </div>`,
                            iconSize: [50, 24],
                            iconAnchor: [25, 12]
                        })}
                    >
                        <Popup>
                            <div style={{ padding: '4px', minWidth: '150px' }}>
                                <strong style={{ color: '#3B82F6' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>{point.icon || 'cloud'}</span>
                                    {' '}Weather at {point.location}
                                </strong>
                                <div style={{ marginTop: '8px', fontSize: '0.9em', color: '#374151' }}>
                                    <div><strong>{point.conditions}</strong></div>
                                    <div>Temperature: {point.temperature}°F (feels like {point.feels_like}°F)</div>
                                    <div>Wind: {point.wind_speed} mph</div>
                                    <div>Humidity: {point.humidity}%</div>
                                </div>
                                {point.alerts && point.alerts.length > 0 && (
                                    <div style={{ marginTop: '8px', padding: '6px', background: '#FEF2F2', borderRadius: '6px', fontSize: '0.8em' }}>
                                        {point.alerts.map((alert, i) => (
                                            <div key={i} style={{ color: alert.severity === 'danger' ? '#DC2626' : '#F59E0B' }}>
                                                ⚠️ {alert.message}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Traffic Incident Markers */}
                {showTraffic && trafficData?.incidents?.map((incident, idx) => (
                    <Marker 
                        key={`traffic-${idx}`} 
                        position={[incident.lat, incident.lon]}
                        icon={L.divIcon({
                            className: 'traffic-marker',
                            html: `<div style="background: ${incident.severity >= 2 ? '#EF4444' : '#F59E0B'}; width: 10px; height: 10px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3);"></div>`,
                            iconSize: [14, 14],
                            iconAnchor: [7, 7]
                        })}
                    >
                        <Popup>
                            <div style={{ padding: '4px', minWidth: '180px' }}>
                                <strong style={{ color: incident.severity >= 2 ? '#EF4444' : '#F59E0B' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '14px', verticalAlign: 'middle' }}>
                                        {incident.type === 'construction' ? 'construction' : 'warning'}
                                    </span>
                                    {' '}{incident.type?.replace('_', ' ').toUpperCase()}
                                </strong>
                                <div style={{ marginTop: '6px', fontSize: '0.9em', color: '#374151' }}>
                                    {incident.description}
                                </div>
                                {incident.delay_minutes > 0 && (
                                    <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#64748B' }}>
                                        Est. delay: {incident.delay_minutes} mins
                                    </div>
                                )}
                                {incident.is_estimate && (
                                    <div style={{ marginTop: '4px', fontSize: '0.7em', color: '#9CA3AF', fontStyle: 'italic' }}>
                                        * Estimated based on typical conditions
                                    </div>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Fuel Station Markers */}
                {showFuelStations && fuelStations.map((station, idx) => (
                    <Marker 
                        key={`fuel-${station.id || idx}`} 
                        position={[station.lat, station.lon]}
                        icon={L.divIcon({
                            className: 'fuel-station-marker',
                            html: `<div style="background: #F59E0B; width: 20px; height: 20px; border-radius: 4px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                                <span style="color: white; font-size: 12px; font-weight: bold;">⛽</span>
                            </div>`,
                            iconSize: [24, 24],
                            iconAnchor: [12, 12]
                        })}
                    >
                        <Popup>
                            <div style={{ padding: '4px', minWidth: '200px' }}>
                                <strong style={{ color: '#F59E0B', fontSize: '1.1em' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>local_gas_station</span>
                                    {' '}{station.name || 'Fuel Station'}
                                </strong>
                                {station.brand && station.brand !== station.name && (
                                    <div style={{ fontSize: '0.9em', color: '#64748B', marginTop: '2px' }}>
                                        {station.brand}
                                    </div>
                                )}
                                {station.address && (
                                    <div style={{ marginTop: '6px', fontSize: '0.85em', color: '#374151' }}>
                                        📍 {station.address}
                                    </div>
                                )}
                                {station.hours && station.hours !== 'Unknown' && (
                                    <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#374151' }}>
                                        🕐 {station.hours}
                                    </div>
                                )}
                                {station.phone && (
                                    <div style={{ marginTop: '4px', fontSize: '0.85em', color: '#374151' }}>
                                        📞 {station.phone}
                                    </div>
                                )}
                                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {station.amenities?.diesel && (
                                        <span style={{ background: '#10B981', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>DIESEL</span>
                                    )}
                                    {station.amenities?.truck_stop && (
                                        <span style={{ background: '#3B82F6', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>TRUCK STOP</span>
                                    )}
                                    {station.amenities?.ev_charging && (
                                        <span style={{ background: '#8B5CF6', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>EV</span>
                                    )}
                                    {station.amenities?.hgv && (
                                        <span style={{ background: '#6366F1', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>HGV</span>
                                    )}
                                </div>
                                <div style={{ marginTop: '6px', fontSize: '0.7em', color: '#10B981' }}>
                                    ✓ Data from {station.source === 'nrel' ? 'NREL' : 'OpenStreetMap'}
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                ))}

                {/* Rest Area Markers */}
                {showRestAreas && restAreas.map((area, idx) => (
                    <Marker 
                        key={`rest-${area.id || idx}`} 
                        position={[area.lat, area.lon]}
                        icon={L.divIcon({
                            className: 'rest-area-marker',
                            html: `<div style="background: #8B5CF6; width: 20px; height: 20px; border-radius: 4px; border: 2px solid white; box-shadow: 0 2px 6px rgba(0,0,0,0.3); display: flex; align-items: center; justify-content: center;">
                                <span style="color: white; font-size: 11px; font-weight: bold;">P</span>
                            </div>`,
                            iconSize: [24, 24],
                            iconAnchor: [12, 12]
                        })}
                    >
                        <Popup>
                            <div style={{ padding: '4px', minWidth: '180px' }}>
                                <strong style={{ color: '#8B5CF6', fontSize: '1.1em' }}>
                                    <span className="material-symbols-outlined" style={{ fontSize: '16px', verticalAlign: 'middle' }}>local_parking</span>
                                    {' '}{area.name || 'Rest Area'}
                                </strong>
                                <div style={{ marginTop: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                    {area.amenities?.toilets && (
                                        <span style={{ background: '#10B981', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>RESTROOMS</span>
                                    )}
                                    {area.amenities?.truck_parking && (
                                        <span style={{ background: '#3B82F6', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>TRUCK PARKING</span>
                                    )}
                                    {area.amenities?.shelter && (
                                        <span style={{ background: '#6366F1', color: 'white', padding: '2px 6px', borderRadius: '4px', fontSize: '0.7em' }}>SHELTER</span>
                                    )}
                                </div>
                                <div style={{ marginTop: '6px', fontSize: '0.7em', color: '#10B981' }}>
                                    ✓ Data from OpenStreetMap
                                </div>
                            </div>
                        </Popup>
                    </Marker>
                ))}
            </MapContainer>

            {/* Layer Toggle Controls */}
            {tripPlan && (
                <div className="map-layer-controls">
                    <button 
                        className={`layer-toggle ${showFuelStations ? 'active' : ''}`}
                        onClick={() => setShowFuelStations(!showFuelStations)}
                        title={`Fuel Stations ${fuelStations.length > 0 ? `(${fuelStations.length})` : ''}`}
                    >
                        <span className="material-symbols-outlined">local_gas_station</span>
                    </button>
                    <button 
                        className={`layer-toggle ${showRestAreas ? 'active' : ''}`}
                        onClick={() => setShowRestAreas(!showRestAreas)}
                        title={`Rest Areas ${restAreas.length > 0 ? `(${restAreas.length})` : ''}`}
                    >
                        <span className="material-symbols-outlined">local_parking</span>
                    </button>
                    <button 
                        className={`layer-toggle ${showWeather ? 'active' : ''}`}
                        onClick={() => setShowWeather(!showWeather)}
                        title="Toggle Weather"
                    >
                        <span className="material-symbols-outlined">cloud</span>
                    </button>
                    <button 
                        className={`layer-toggle ${showTraffic ? 'active' : ''}`}
                        onClick={() => setShowTraffic(!showTraffic)}
                        title="Toggle Traffic"
                    >
                        <span className="material-symbols-outlined">traffic</span>
                    </button>
                </div>
            )}

            {/* Fuel Station Error Alert */}
            {fuelStationError && showFuelError && (
                <div className="fuel-error-alert">
                    <span className="material-symbols-outlined">warning</span>
                    <span>{fuelStationError}</span>
                    <button 
                        className="alert-dismiss-btn"
                        onClick={() => setShowFuelError(false)}
                        title="Dismiss"
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>
            )}

            {/* Weather & Traffic Summary Panel */}
            {tripPlan && showRouteAlerts && (weatherData?.alerts?.length > 0 || trafficData?.incidents?.length > 0) && (
                <div className="route-alerts-panel">
                    <div className="alerts-header">
                        <h4>
                            <span className="material-symbols-outlined">warning</span>
                            Route Alerts
                        </h4>
                        <button 
                            className="alert-dismiss-btn"
                            onClick={() => setShowRouteAlerts(false)}
                            title="Dismiss alerts"
                        >
                            <span className="material-symbols-outlined">close</span>
                        </button>
                    </div>
                    {weatherData?.alerts?.map((alert, i) => (
                        <div key={`wa-${i}`} className={`alert-item ${alert.severity}`}>
                            <span className="material-symbols-outlined">
                                {alert.type === 'HIGH_WIND' ? 'air' : 
                                 alert.type === 'SNOW' ? 'weather_snowy' :
                                 alert.type === 'ICE' ? 'ac_unit' : 'cloud'}
                            </span>
                            <span>{alert.message}</span>
                        </div>
                    ))}
                    {trafficData?.message && (
                        <div className={`alert-item ${trafficData.severity === 'clear' ? 'clear' : 'caution'}`}>
                            <span className="material-symbols-outlined">traffic</span>
                            <span>{trafficData.message}</span>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MapWorkspace;
